import type { 
  PayCycle, 
  PayStructure, 
  PayrollRun, 
  Payslip, 
  PayrollPayment,
  PayStructureFormData,
  PayrollRunFormData,
  PayrollPaymentFormData
} from '../types';
import { postSalaryPaymentVouchers } from '../../../services/payrollAccountingService';

const mockDatabases = {
  payCycles: [] as PayCycle[],
  payStructures: [] as PayStructure[],
  payrollRuns: [] as PayrollRun[],
  payslips: [] as Payslip[],
  payrollPayments: [] as PayrollPayment[]
};

if (mockDatabases.payCycles.length === 0) {
  mockDatabases.payCycles.push({
    id: 'cycle-1',
    name: 'Monthly Cycle',
    frequency: 'monthly',
    start_date: '2024-01-01',
    end_date: '2024-12-31',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
}

export const payCycleService = {
  async getAll(): Promise<PayCycle[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...mockDatabases.payCycles];
  },

  async getById(id: string): Promise<PayCycle | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payCycles.find(cycle => cycle.id === id) || null;
  },

  async create(data: Omit<PayCycle, 'id' | 'created_at' | 'updated_at'>): Promise<PayCycle> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const cycle: PayCycle = {
      ...data,
      id: `cycle-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockDatabases.payCycles.push(cycle);
    return cycle;
  }
};

export const payStructureService = {
  async getAll(): Promise<PayStructure[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...mockDatabases.payStructures];
  },

  async getByStaffId(staffId: string): Promise<PayStructure | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payStructures.find(
      structure => structure.staff_id === staffId && structure.status === 'active'
    ) || null;
  },

  async create(data: PayStructureFormData): Promise<PayStructure> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    mockDatabases.payStructures.forEach(structure => {
      if (structure.staff_id === data.staff_id && structure.status === 'active') {
        structure.status = 'inactive';
        structure.effective_to = new Date().toISOString().split('T')[0];
      }
    });

    const structure: PayStructure = {
      id: `structure-${Date.now()}`,
      staff_id: data.staff_id,
      basic_salary: data.basic_salary,
      salary_type: data.salary_type,
      hourly_rate: data.hourly_rate,
      allowances: {
        house_rent: data.house_rent || 0,
        transport: data.transport || 0,
        medical: data.medical || 0,
        other: data.other_allowances || 0
      },
      deductions: {
        tax: data.tax || 0,
        provident_fund: data.provident_fund || 0,
        insurance: data.insurance || 0,
        loan: data.loan || 0,
        other: data.other_deductions || 0
      },
      overtime_rate: data.overtime_rate,
      effective_from: data.effective_from,
      effective_to: data.effective_to,
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    mockDatabases.payStructures.push(structure);
    return structure;
  },

  async update(id: string, data: Partial<PayStructureFormData>): Promise<PayStructure> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const index = mockDatabases.payStructures.findIndex(structure => structure.id === id);
    if (index === -1) throw new Error('Pay structure not found');

    const existing = mockDatabases.payStructures[index];
    const updated: PayStructure = {
      ...existing,
      basic_salary: data.basic_salary ?? existing.basic_salary,
      salary_type: data.salary_type ?? existing.salary_type,
      hourly_rate: data.hourly_rate ?? existing.hourly_rate,
      allowances: {
        house_rent: data.house_rent ?? existing.allowances.house_rent ?? 0,
        transport: data.transport ?? existing.allowances.transport ?? 0,
        medical: data.medical ?? existing.allowances.medical ?? 0,
        other: data.other_allowances ?? existing.allowances.other ?? 0
      },
      deductions: {
        tax: data.tax ?? existing.deductions.tax ?? 0,
        provident_fund: data.provident_fund ?? existing.deductions.provident_fund ?? 0,
        insurance: data.insurance ?? existing.deductions.insurance ?? 0,
        loan: data.loan ?? existing.deductions.loan ?? 0,
        other: data.other_deductions ?? existing.deductions.other ?? 0
      },
      overtime_rate: data.overtime_rate ?? existing.overtime_rate,
      effective_from: data.effective_from ?? existing.effective_from,
      effective_to: data.effective_to ?? existing.effective_to,
      updated_at: new Date().toISOString()
    };

    mockDatabases.payStructures[index] = updated;
    return updated;
  }
};

export const payrollRunService = {
  async getAll(): Promise<PayrollRun[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...mockDatabases.payrollRuns].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  async getById(id: string): Promise<PayrollRun | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payrollRuns.find(run => run.id === id) || null;
  },

  async create(data: PayrollRunFormData): Promise<PayrollRun> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const run: PayrollRun = {
      id: `run-${Date.now()}`,
      cycle_id: data.cycle_id,
      period_start: data.period_start,
      period_end: data.period_end,
      run_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      total_gross: 0,
      total_deductions: 0,
      total_net: 0,
      staff_count: 0,
      notes: data.notes,
      created_by: 'current-user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    mockDatabases.payrollRuns.push(run);
    return run;
  },

  async updateStatus(id: string, status: PayrollRun['status'], approvedBy?: string): Promise<PayrollRun> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const index = mockDatabases.payrollRuns.findIndex(run => run.id === id);
    if (index === -1) throw new Error('Payroll run not found');

    const updated = {
      ...mockDatabases.payrollRuns[index],
      status,
      approved_by: approvedBy,
      approved_at: status === 'approved' ? new Date().toISOString() : undefined,
      updated_at: new Date().toISOString()
    };

    mockDatabases.payrollRuns[index] = updated;
    return updated;
  }
};

export const payslipService = {
  async getAll(): Promise<Payslip[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...mockDatabases.payslips];
  },

  async getByRunId(runId: string): Promise<Payslip[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payslips.filter(slip => slip.payroll_run_id === runId);
  },

  async getByStaffId(staffId: string): Promise<Payslip[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payslips.filter(slip => slip.staff_id === staffId);
  },

  async getById(id: string): Promise<Payslip | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payslips.find(slip => slip.id === id) || null;
  },

  async generatePayslips(runId: string, staffList: any[]): Promise<Payslip[]> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const run = await payrollRunService.getById(runId);
    if (!run) throw new Error('Payroll run not found');

    const payslips: Payslip[] = [];
    let totalGross = 0;
    let totalDeductions = 0;
    let totalNet = 0;

    for (const staff of staffList) {
      const structure = await payStructureService.getByStaffId(staff.id);
      if (!structure) continue;

      const basicSalary = structure.basic_salary;
      
      const houseRent = structure.allowances.house_rent || 0;
      const transport = structure.allowances.transport || 0;
      const medical = structure.allowances.medical || 0;
      const otherAllowances = structure.allowances.other || 0;
      
      const overtimeHours = Math.floor(Math.random() * 10);
      const overtimeAmount = overtimeHours * (structure.overtime_rate || 0);
      
      const grossEarnings = basicSalary + houseRent + transport + medical + otherAllowances + overtimeAmount;
      
      const tax = structure.deductions.tax || 0;
      const pf = structure.deductions.provident_fund || 0;
      const insurance = structure.deductions.insurance || 0;
      const loan = structure.deductions.loan || 0;
      const otherDeductions = structure.deductions.other || 0;
      const unpaidLeave = 0; // Mock data
      
      const totalDeductionsAmount = tax + pf + insurance + loan + otherDeductions + unpaidLeave;
      const netSalary = Math.round(grossEarnings - totalDeductionsAmount);

      const payslip: Payslip = {
        id: `slip-${Date.now()}-${staff.id}`,
        payroll_run_id: runId,
        staff_id: staff.id,
        employee_code: staff.empCode || staff.id,
        employee_name: staff.name,
        department: staff.department || 'General',
        designation: staff.designation || 'Staff',
        period_start: run.period_start,
        period_end: run.period_end,
        
        basic_salary: basicSalary,
        house_rent_allowance: houseRent,
        transport_allowance: transport,
        medical_allowance: medical,
        other_allowances: otherAllowances,
        overtime_hours: overtimeHours,
        overtime_amount: overtimeAmount,
        gross_earnings: grossEarnings,
        
        tax_deduction: tax,
        provident_fund: pf,
        insurance_deduction: insurance,
        loan_deduction: loan,
        unpaid_leave_deduction: unpaidLeave,
        other_deductions: otherDeductions,
        total_deductions: totalDeductionsAmount,
        
        net_salary: netSalary,
        
        working_days: 30,
        present_days: 28 + Math.floor(Math.random() * 3),
        absent_days: Math.floor(Math.random() * 3),
        late_days: Math.floor(Math.random() * 5),
        
        status: 'draft',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      payslips.push(payslip);
      mockDatabases.payslips.push(payslip);
      
      totalGross += grossEarnings;
      totalDeductions += totalDeductionsAmount;
      totalNet += netSalary;
    }

    await payrollRunService.updateStatus(runId, 'calculated');
    const runIndex = mockDatabases.payrollRuns.findIndex(r => r.id === runId);
    if (runIndex !== -1) {
      mockDatabases.payrollRuns[runIndex] = {
        ...mockDatabases.payrollRuns[runIndex],
        total_gross: totalGross,
        total_deductions: totalDeductions,
        total_net: totalNet,
        staff_count: payslips.length
      };
    }

    return payslips;
  },

  async updateStatus(id: string, status: Payslip['status']): Promise<Payslip> {
    await new Promise(resolve => setTimeout(resolve, 100));
    const index = mockDatabases.payslips.findIndex(slip => slip.id === id);
    if (index === -1) throw new Error('Payslip not found');

    const updated = {
      ...mockDatabases.payslips[index],
      status,
      updated_at: new Date().toISOString()
    };

    mockDatabases.payslips[index] = updated;
    return updated;
  }
};

export const payrollPaymentService = {
  async getAll(): Promise<PayrollPayment[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...mockDatabases.payrollPayments];
  },

  async getByPayslipId(payslipId: string): Promise<PayrollPayment[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockDatabases.payrollPayments.filter(payment => payment.payslip_id === payslipId);
  },

  async create(data: PayrollPaymentFormData): Promise<PayrollPayment> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const payslip = await payslipService.getById(data.payslip_id);
    if (!payslip) throw new Error('Payslip not found');

    const payment: PayrollPayment = {
      id: `payment-${Date.now()}`,
      payslip_id: data.payslip_id,
      staff_id: payslip.staff_id,
      amount: data.amount,
      payment_date: data.payment_date,
      payment_method: data.payment_method,
      reference_number: data.reference_number,
      bank_account: data.bank_account,
      notes: data.notes,
      status: 'completed',
      created_by: 'current-user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    mockDatabases.payrollPayments.push(payment);
    
    await payslipService.updateStatus(data.payslip_id, 'paid');
    const payslipIndex = mockDatabases.payslips.findIndex(s => s.id === data.payslip_id);
    if (payslipIndex !== -1) {
      mockDatabases.payslips[payslipIndex] = {
        ...mockDatabases.payslips[payslipIndex],
        payment_date: data.payment_date,
        payment_method: data.payment_method,
        payment_reference: data.reference_number
      };
    }

    try {
      const period = `${payslip.period_start} to ${payslip.period_end}`;
      const voucherResult = await postSalaryPaymentVouchers(
        payment,
        payslip.employee_name,
        period
      );
      
      if (voucherResult.success) {
        console.log('Accounting vouchers created successfully:', {
          debitVoucherId: voucherResult.debitVoucherId,
          creditVoucherId: voucherResult.creditVoucherId,
          isDuplicate: voucherResult.isDuplicate
        });
      } else {
        console.warn('Failed to create accounting vouchers:', voucherResult.error);
      }
    } catch (error) {
      console.error('Error posting accounting vouchers:', error);
    }

    return payment;
  }
};
